import React, {forwardRef, useState, useMemo, useRef} from 'react'
import Select from 'react-select'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {Box, chakra, Flex, useMultiStyleConfig} from '@chakra-ui/react'
import {DropdownTheme} from '../../theme/components/base/dropdown'
import Option from './sub-components/Option'
import CustomValueContainer from './sub-components/CustomValueContainer'

const ChakraSelect = chakra(Select)

const Dropdown = forwardRef(
    (
        {
            isSearchable,
            isDisabled,
            isInvalid,
            placeholder,
            onChange,
            name,
            label,
            // rules,
            size = 'xl',
            value,
            options,
            variant,
            isFieldRequired = null,
            ariaLabel = '',
            ...rest
        },
        ref,
    ) => {
        const [inputValue, setInputValue] = useState('')
        const [menuIsOpen, setMenuIsOpen] = useState(false)
        const selectRef = useRef(ref || null)

        const styles = useMultiStyleConfig('Dropdown', {
            variant: isInvalid ? 'invalid' : variant,
            size,
        })
        const {formatMessage} = useIntl()

        let objValue

        if (typeof value === 'string') {
            const matchingOption = options?.find((option) => option.value === value)
            objValue = matchingOption || null
        } else {
            objValue = value?.value ? value : null
        }

        const placeholderText = label || placeholder

        // const isGrouped = options.length > 0 && 'options' in options[0]

        const filteredSortedOptions = useMemo(() => {
            //This is commented out since we don't have any grouped options at the moment
            //Left this in case we want to add support for grouped options in the future

            // if (isGrouped) {
            //     return options
            //         .map((group) => {
            //             const groupOptions = [...group.options]
            //             if (!inputValue) {
            //                 groupOptions.sort((a, b) => a.label.localeCompare(b.label))
            //                 return {...group, options: groupOptions}
            //             }
            //             const input = inputValue.toLowerCase()
            //             const filtered = groupOptions.filter((option) =>
            //                 option.label.toLowerCase().includes(input),
            //             )
            //             filtered.sort((a, b) => {
            //                 const aStarts = a.label.toLowerCase().startsWith(input)
            //                 const bStarts = b.label.toLowerCase().startsWith(input)
            //                 if (aStarts && !bStarts) return -1
            //                 if (!aStarts && bStarts) return 1
            //                 return a.label.localeCompare(b.label)
            //             })
            //             return {...group, options: filtered}
            //         })
            //         .filter((group) => group.options.length > 0)
            // }

            if (!inputValue) {
                return [...options].sort((a, b) => a.label.localeCompare(b.label))
            }

            const input = inputValue.toLowerCase()

            const startsWithInput = []
            const containsInput = []

            for (const option of options) {
                const label = option.label.toLowerCase()
                if (label.startsWith(input)) {
                    startsWithInput.push(option)
                } else if (label.includes(input)) {
                    containsInput.push(option)
                }
            }

            startsWithInput.sort((a, b) => a.label.localeCompare(b.label))
            containsInput.sort((a, b) => a.label.localeCompare(b.label))

            return [...startsWithInput, ...containsInput]
        }, [options, inputValue])

        const formattedAriaLabel =
            typeof ariaLabel === 'string' ? ariaLabel : formatMessage(ariaLabel)

        return (
            <Flex
                {...styles.container}
                role="group"
                tabIndex={-1}
                aria-label={formatMessage({
                    defaultMessage: 'Please select a value',
                    id: 'form.error.please_select_a_value',
                })}
                onKeyDown={(e) => {
                    if (!isSearchable && !menuIsOpen && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault()
                        setMenuIsOpen(true)
                    }
                }}
                onBlur={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget)) {
                        setMenuIsOpen(false)
                    }
                }}
            >
                <Box __css={styles} w="100%">
                    <ChakraSelect
                        {...rest}
                        ref={(instance) => {
                            selectRef.current = instance

                            if (typeof ref === 'function') {
                                ref(instance)
                            } else if (ref) {
                                ref.current = instance
                            }
                        }}
                        inputId={`select-${name}`}
                        instanceId={name}
                        id={`container-${name}`}
                        placeholder={placeholderText}
                        components={{
                            IndicatorsContainer: () => null,
                            Option,
                            ValueContainer: (props) => (
                                <CustomValueContainer {...props} menuIsOpen={menuIsOpen} />
                            ),
                        }}
                        className="react-select-container"
                        classNamePrefix="react-select"
                        isSearchable={isSearchable ?? false}
                        value={objValue}
                        inputValue={inputValue}
                        onInputChange={(inputValue) => setInputValue(inputValue)}
                        isDisabled={isDisabled ?? false}
                        menuIsOpen={menuIsOpen}
                        onMenuOpen={() => {
                            setMenuIsOpen(true)
                            if (selectRef.current && selectRef.current.inputRef) {
                                selectRef.current.inputRef.focus()
                            }
                        }}
                        onMenuClose={() => setMenuIsOpen(false)}
                        onChange={(newValue, actionMeta) => {
                            onChange?.(newValue, actionMeta)
                            requestAnimationFrame(() => {
                                selectRef.current?.focus?.()
                            })
                        }}
                        onKeyDown={(e) => {
                            // Prevent menu from closing on Tab, treat it like ArrowDown
                            if (e.key === 'Tab' && menuIsOpen) {
                                e.preventDefault()
                                // Simulate arrow down
                                const event = new KeyboardEvent('keydown', {
                                    key: 'ArrowDown',
                                    bubbles: true,
                                })
                                e.target.dispatchEvent(event)
                            }
                        }}
                        theme={DropdownTheme(styles)}
                        required={isFieldRequired}
                        aria-label={
                            ariaLabel
                                ? formattedAriaLabel
                                : formatMessage({
                                      defaultMessage: 'Please select a value',
                                      id: 'form.error.please_select_a_value',
                                  })
                        }
                        options={filteredSortedOptions}
                    />
                </Box>
            </Flex>
        )
    },
)

Dropdown.displayName = 'Dropdown'
Dropdown.propTypes = {
    placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    ariaLabel: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            defaultMessage: PropTypes.string.isRequired,
            description: PropTypes.string,
            values: PropTypes.object,
        }),
    ]),
    value: PropTypes.any,
    isSearchable: PropTypes.bool,
    isDisabled: PropTypes.bool,
    isInvalid: PropTypes.bool,
    name: PropTypes.string.isRequired,
    label: PropTypes.string,
    options: PropTypes.array.isRequired,
    onChange: PropTypes.func,
    variant: PropTypes.string,
    rules: PropTypes.object,
    isFieldRequired: PropTypes.bool,
    size: PropTypes.oneOf(['small', 'base', 'large', 'xl']),
}

export default Dropdown
